import { ArrowRight, Github, Loader2, Mail } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { GlassPanel, Pill, Surface } from "@/components/glass";
import { analytics } from "@/lib/analytics";
import { signIn } from "@/lib/auth-client";
import { humanizeAuthError } from "@/lib/auth-errors";

// All provider sign-ins funnel back to /login on failure with the
// better-auth error message in the `auth_error` query param. The page
// reads it, shows a banner, and rewrites the URL so a refresh doesn't
// keep the banner up.
const ERROR_REDIRECT = `${window.location.origin}/login`;

export default function Login() {
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const raw = params.get("error") ?? params.get("auth_error");
		if (raw) {
			setError(humanizeAuthError(raw));
			params.delete("error");
			params.delete("auth_error");
			const qs = params.toString();
			window.history.replaceState(
				null,
				"",
				`/login${qs ? `?${qs}` : ""}`,
			);
		}
	}, []);

	return (
		<Surface className="flex items-center justify-center p-4 min-h-screen">
			<div className="w-full max-w-sm flex flex-col items-stretch gap-3">
				{error && <AuthErrorNotice message={error} />}
				<GlassPanel className="p-8 text-center space-y-6">
					<div className="space-y-3">
						<div className="flex justify-center">
							<img
								src="/logo.svg"
								alt="renketsu"
								className="h-6 w-auto opacity-95"
							/>
						</div>
						<p className="text-[13px] text-[var(--color-text-muted)]">
							The AI-powered context layer for your business.
						</p>
					</div>
					<div className="flex flex-col gap-2 items-center">
						<Pill onClick={() => startProviderSignIn("github", setError)}>
							<Github className="w-3.5 h-3.5" />
							Continue with GitHub
						</Pill>
						<Pill onClick={() => startProviderSignIn("google", setError)}>
							<GoogleG className="w-3.5 h-3.5" />
							Continue with Google
						</Pill>
						<Pill onClick={() => startCodespringSignIn(setError)}>
							<CodespringMark className="w-3.5 h-3.5" />
							Continue with Codespring
						</Pill>
					</div>

					<OrDivider />

					<MagicLinkForm onError={setError} />
				</GlassPanel>
			</div>
		</Surface>
	);
}

function OrDivider() {
	return (
		<div className="flex items-center gap-3 text-[11px] text-[var(--color-text-dim)] uppercase tracking-wider">
			<div className="flex-1 h-px bg-[var(--color-border)]" />
			<span>or</span>
			<div className="flex-1 h-px bg-[var(--color-border)]" />
		</div>
	);
}

function MagicLinkForm({
	onError,
}: {
	onError: (msg: string | null) => void;
}) {
	const [email, setEmail] = useState("");
	const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");

	async function onSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const trimmed = email.trim();
		if (!trimmed) return;
		onError(null);
		setStatus("sending");
		analytics.capture("signin_attempted", { provider: "magic_link" });
		try {
			const res = await signIn.magicLink({
				email: trimmed,
				callbackURL: "/",
			});
			if ((res as { error?: { message?: string } }).error) {
				onError(
					(res as { error: { message?: string } }).error.message ??
						"Couldn't send the magic link.",
				);
				setStatus("idle");
				return;
			}
			setStatus("sent");
		} catch (err) {
			onError(err instanceof Error ? err.message : "Couldn't send the magic link.");
			setStatus("idle");
		}
	}

	if (status === "sent") {
		return (
			<div className="text-[12px] text-[var(--color-text-muted)] leading-snug">
				<div className="text-[var(--color-text)] mb-1">
					Check your inbox
				</div>
				A sign-in link is on its way to{" "}
				<span className="mono text-[var(--color-text)]">{email}</span>. It
				expires in 10 minutes.
			</div>
		);
	}

	const submitDisabled = status === "sending" || !email.trim();

	return (
		<form onSubmit={onSubmit} className="flex flex-col gap-2 text-left">
			<div className="relative">
				<Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--color-text-dim)] pointer-events-none" />
				<input
					type="email"
					required
					autoComplete="email"
					placeholder="you@example.com"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					disabled={status === "sending"}
					className="
						w-full h-9 pl-9 pr-3 rounded-[var(--radius-pill)]
						bg-[var(--color-surface-solid)]
						border border-[var(--color-border)]
						text-[13px] text-[var(--color-text)]
						placeholder:text-[var(--color-text-dim)]
						transition-colors
						hover:border-[var(--color-border-hi)]
						focus:border-[var(--color-border-hi)]
						disabled:opacity-50
					"
				/>
			</div>
			<button
				type="submit"
				disabled={submitDisabled}
				className="
					inline-flex items-center justify-center gap-1.5 h-9 px-3
					rounded-[var(--radius-pill)] text-[12px] font-medium
					bg-[var(--color-text)] text-[var(--color-bg)]
					border border-[var(--color-text)]
					transition-colors
					hover:bg-white hover:border-white
					appearance-none
					outline-none focus:outline-none focus-visible:outline-none
					disabled:opacity-50 disabled:cursor-not-allowed
				"
			>
				{status === "sending" ? (
					<>
						<Loader2 className="w-3.5 h-3.5 animate-spin" />
						Sending
					</>
				) : (
					<>
						Send magic link
						<ArrowRight className="w-3.5 h-3.5" />
					</>
				)}
			</button>
		</form>
	);
}

/**
 * Built-in `signIn.social` and `signIn.oauth2` should auto-navigate
 * to the provider on success, but the genericOAuthClient + the way
 * we previously fire-and-forget the promise lets some redirects get
 * swallowed (saw "Continue with Codespring" go silent on prod).
 * Awaiting the result and reading `data.url` covers every shape and
 * navigates explicitly. Errors surface through the same banner.
 */
async function startProviderSignIn(
	provider: "github" | "google",
	setError: (msg: string | null) => void,
) {
	analytics.capture("signin_attempted", { provider });
	try {
		const res = await signIn.social({
			provider,
			callbackURL: "/",
			errorCallbackURL: ERROR_REDIRECT,
		});
		handleAuthResult(res, setError);
	} catch (e) {
		setError(humanizeAuthMessage(e));
	}
}

async function startCodespringSignIn(setError: (msg: string | null) => void) {
	analytics.capture("signin_attempted", { provider: "codespring" });
	try {
		const res = await signIn.oauth2({
			providerId: "codespring",
			callbackURL: "/",
			errorCallbackURL: ERROR_REDIRECT,
		});
		handleAuthResult(res, setError);
	} catch (e) {
		setError(humanizeAuthMessage(e));
	}
}

function handleAuthResult(
	res: unknown,
	setError: (msg: string | null) => void,
) {
	const data = (res as { data?: { url?: string; redirect?: boolean } }).data;
	const err = (res as { error?: { message?: string } }).error;
	if (err?.message) {
		setError(err.message);
		return;
	}
	if (data?.url) {
		window.location.assign(data.url);
		return;
	}
	// No URL, no error — the SDK already navigated (some paths set
	// window.location internally). Nothing to do.
}

function humanizeAuthMessage(e: unknown): string {
	if (e instanceof Error) return e.message;
	return "Sign-in didn't start. Try again.";
}

/**
 * Sits above the auth card. Same surface treatment as the rest of
 * the app (subtle border, glass-style fill) so it reads as part of
 * the layout, not a browser alert dropped on top. A small red dot
 * carries the "error" semantic without painting the whole row red.
 */
function AuthErrorNotice({ message }: { message: string }) {
	return (
		<div
			className="
				rounded-[12px] px-4 py-3 text-[12px] leading-snug text-left
				border border-[var(--color-border)]
				bg-[var(--color-surface)]
				text-[var(--color-text)]
			"
		>
			<div className="flex items-start gap-2.5">
				<span
					aria-hidden="true"
					className="mt-[5px] w-1.5 h-1.5 rounded-full bg-[var(--color-danger)] shrink-0"
				/>
				<div className="flex-1">{message}</div>
			</div>
		</div>
	);
}

/**
 * Codespring brand mark. The product's canonical logo doesn't have
 * an SVG variant in-repo, so we ship the PNG from
 * apps/web/public/codespring-mark.png. Render as a square image
 * with rounded corners to match the other Pill icons.
 */
function CodespringMark({ className }: { className?: string }) {
	return (
		<img
			src="/codespring-mark.png"
			alt=""
			aria-hidden="true"
			className={className}
			style={{ borderRadius: "3px" }}
		/>
	);
}

/**
 * Google "G" mark — inlined so we don't pull a new icon package for
 * one glyph. Uses the canonical 4-color logo on dark backgrounds.
 */
function GoogleG({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 18 18"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
		>
			<title>Google</title>
			<path
				fill="#4285F4"
				d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
			/>
			<path
				fill="#34A853"
				d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.836.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
			/>
			<path
				fill="#FBBC05"
				d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
			/>
			<path
				fill="#EA4335"
				d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
			/>
		</svg>
	);
}
